import { ReactNode, useState } from "react"
import PlayerAvatar from "@/components/player/PlayerAvatar"
import ElementIconImage from "@/components/common/ElementIconImage"
import LeagueLabel from "@/components/guardians/LeagueLabel"
import GuardianRankChip from "@/components/guardians/GuardianRankChip"
import {
  AFFILIATION_ELEMENT_TITLES,
  getPassportElementGradient,
} from "@/constants/affiliationInfo"
import { harmonizeCardLook } from "@/constants/harmonizeCardLook"
import { resolveProfileCardBg } from "@/constants/profileCardBackgrounds"
import { resolveProfileNicknameColor } from "@/constants/profileNicknameColors"
import { resolveProfileNicknamePulse } from "@/constants/profileNicknamePulses"
import {
  AcademicLeague,
  AffiliationPassive,
  CardElement,
  DigitalPassport,
  PvpStats,
} from "@/types/battle"
import { formatWinRate } from "@/utils/pvpStatsFormat"

export interface PassportCardViewModel {
  nickname: string
  publicGameCode: string
  affiliationElement?: CardElement | null
  avatarVariant?: number | null
  affiliationPassive?: AffiliationPassive | null
  pvpStats?: PvpStats
  academicLeague?: AcademicLeague | string | null
  profileFrameKey?: string | null
  cityLabel?: string | null
  globalPts?: number
  localPts?: number
  cardsCount?: number
  guardianRank?: number
  guildTag?: string | null
  guildName?: string | null
  equippedCardBgKey?: string | null
  equippedNickColorKey?: string | null
  equippedNickPulseKey?: string | null
}

export function passportCardFromDigitalPassport(
  passport: DigitalPassport,
  cityLabel?: string
): PassportCardViewModel {
  return {
    nickname: passport.nickname,
    publicGameCode: passport.publicGameCode,
    affiliationElement: passport.affiliationElement,
    avatarVariant: passport.avatarVariant ?? 1,
    affiliationPassive: passport.affiliationPassive,
    pvpStats: passport.pvpStats,
    academicLeague: passport.academicLeague,
    profileFrameKey: passport.profileFrameKey,
    cityLabel,
    guardianRank:
      passport.guardianRankInfo?.rank ?? passport.activeTime?.guardianRank,
    guildTag: passport.guild?.tag ?? null,
    guildName: passport.guild?.name ?? null,
    equippedCardBgKey: passport.equippedCardBgKey ?? null,
    equippedNickColorKey: passport.equippedNickColorKey ?? null,
    equippedNickPulseKey: passport.equippedNickPulseKey ?? null,
  }
}

function FooterStat({
  label,
  value,
  wide,
}: {
  label: string
  value: ReactNode
  wide?: boolean
}) {
  return (
    <div className={`flex flex-col items-center ${wide ? "min-w-[52px] flex-1" : "min-w-9"}`}>
      {typeof value === "string" ? (
        <span className="max-w-full truncate text-[13px] font-extrabold text-white">
          {value}
        </span>
      ) : (
        value
      )}
      <span className="text-[9px] font-bold uppercase text-white/45">{label}</span>
    </div>
  )
}

export default function GuardianPassportCard({
  profile,
  showRankBadge = false,
  applyLocalCardSkin = false,
}: {
  profile: PassportCardViewModel
  showRankBadge?: boolean
  applyLocalCardSkin?: boolean
}) {
  const [elementInfoOpen, setElementInfoOpen] = useState(false)

  const bgKey = applyLocalCardSkin ? profile.equippedCardBgKey : null
  const nickKey = applyLocalCardSkin ? profile.equippedNickColorKey : null
  const nickPulseKey = applyLocalCardSkin ? profile.equippedNickPulseKey : null

  const bg = resolveProfileCardBg(bgKey as never)
  const nick = resolveProfileNicknameColor(nickKey as never)
  const nickPulse = resolveProfileNicknamePulse(nickPulseKey as never)
  const look = harmonizeCardLook({ bg, nick })

  const element = profile.affiliationElement
  const baseGradient = getPassportElementGradient(element)
  const gradient = bg?.style?.gradient ?? baseGradient
  const gradientCss = `linear-gradient(135deg, ${gradient.join(", ")})`

  const winRate = formatWinRate(profile.pvpStats?.winRate)
  const pvpWins = profile.pvpStats?.pvpWins ?? 0
  const pvpLosses = profile.pvpStats?.pvpLosses ?? 0
  const pvpDraws = profile.pvpStats?.pvpDraws ?? 0
  const nickColor = look.nickColor
  const nickLabel = `${profile.guildTag ? `[${profile.guildTag}] ` : ""}${profile.nickname}`

  return (
    <>
      <div
        className="relative w-full overflow-hidden rounded-[20px] border border-white/10 p-4"
        style={{ background: gradientCss }}
      >
        {bg?.style?.overlayColor ? (
          <div
            className="pointer-events-none absolute inset-0 rounded-[20px]"
            style={{ backgroundColor: bg.style.overlayColor }}
          />
        ) : (
          <div className="pointer-events-none absolute inset-0 rounded-[20px] bg-white/[0.04]" />
        )}

        <div className="relative flex flex-row items-start gap-3">
          <button
            type="button"
            className="shrink-0 border-0 bg-transparent p-0"
            onClick={() => element && setElementInfoOpen(true)}
            disabled={!element}
          >
            <PlayerAvatar
              element={element}
              avatarVariant={profile.avatarVariant ?? 1}
              nickname={profile.nickname}
              academicLeague={profile.academicLeague as AcademicLeague | null}
              profileFrameKey={profile.profileFrameKey ?? null}
              guardianRank={profile.guardianRank ?? null}
              size="md"
            />
          </button>

          <div className="min-w-0 flex-1">
            <div
              className={`truncate text-lg font-black leading-tight ${nickPulse ? "animate-pulse" : ""}`}
              style={{ color: nickColor ?? undefined }}
            >
              {nickLabel}
            </div>
            {profile.guildName ? (
              <p className="truncate text-[11px] font-bold tracking-wide text-white/55">
                Гильдия: {profile.guildName}
              </p>
            ) : null}
            <p className="truncate text-[11px] font-bold tracking-wide text-white/55">
              {profile.publicGameCode || "—"}
            </p>

            <div className="mt-0.5 flex flex-row items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 flex-row flex-wrap items-center gap-1.5">
                {profile.guardianRank != null && showRankBadge ? (
                  <>
                    <GuardianRankChip rank={profile.guardianRank} />
                    {element ? (
                      <span className="text-sm font-bold text-white/35">·</span>
                    ) : null}
                  </>
                ) : null}
                {element ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border-0 bg-transparent p-0"
                    onClick={() => setElementInfoOpen(true)}
                  >
                    <ElementIconImage element={element} size={30} />
                    <span className="text-xs font-extrabold text-white/85">
                      {AFFILIATION_ELEMENT_TITLES[element]}
                    </span>
                  </button>
                ) : (
                  <span className="text-[11px] font-semibold text-white/45">
                    Стихия не выбрана
                  </span>
                )}
              </div>
              <div className="shrink-0 text-right">
                <div
                  className="text-[22px] font-black leading-none text-white"
                  style={look.winrateColor ? { color: look.winrateColor } : undefined}
                >
                  {winRate}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-white/55">
                  Winrate
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-3 flex flex-row flex-wrap items-center gap-2 border-t border-white/10 pt-2">
          <FooterStat label="В" value={String(pvpWins)} />
          <FooterStat label="П" value={String(pvpLosses)} />
          {pvpDraws > 0 ? <FooterStat label="Н" value={String(pvpDraws)} /> : null}
          <div className="mx-0.5 h-6 w-px bg-white/15" />
          <FooterStat
            label="Лига"
            wide
            value={
              <LeagueLabel
                league={profile.academicLeague as AcademicLeague | null}
                iconSize={22}
              />
            }
          />
          {profile.cityLabel && profile.cityLabel !== "—" ? (
            <FooterStat label="Город" value={profile.cityLabel} wide />
          ) : null}
        </div>

        {profile.affiliationPassive?.title ? (
          <p className="relative mt-2 truncate text-[11px] font-bold text-white/70">
            {profile.affiliationPassive.title}
          </p>
        ) : null}
      </div>

      {elementInfoOpen && element ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center"
          role="dialog"
          onClick={() => setElementInfoOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-guardians-bg p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-extrabold text-white">
              {AFFILIATION_ELEMENT_TITLES[element]}
            </h3>
            {profile.affiliationPassive?.title ? (
              <p className="mt-1 text-sm font-bold text-guardians-accent">
                {profile.affiliationPassive.title}
              </p>
            ) : null}
            {profile.affiliationPassive?.description ? (
              <p className="mt-2 text-sm text-white/65">
                {profile.affiliationPassive.description}
              </p>
            ) : null}
            <button
              type="button"
              className="mt-4 w-full rounded-xl bg-white/10 py-2 text-sm font-semibold"
              onClick={() => setElementInfoOpen(false)}
            >
              Закрыть
            </button>
          </div>
        </div>
      ) : null}
    </>
  )
}
